// SettingsPanel.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import ProfileSection from './ProfileSection';
import APIKeysSection from './APIKeysSection';
import SubscriptionSection from './SubscriptionSection';
import { CatLogo } from '@/components/CatLogo';
import { useTheme } from '@/managers/ThemeProvider';

export const TABS = {
  PROFILE: 'profile',
  API_KEYS: 'api_keys',
  SUBSCRIPTION: 'subscription'
};

const TAB_DISPLAY_NAMES = {
  PROFILE: 'Profile & Preferences',
  API_KEYS: 'API Keys',
  SUBSCRIPTION: 'Subscription'
};

export function SettingsPanel({
  isOpen,
  onClose,
  initialTab = TABS.PROFILE
}) {
  const [authState, setAuthState] = useState(authManager.getState());
  const normalizedInitialTab = initialTab === 'preferences' ? TABS.PROFILE : initialTab;
  const [activeTab, setActiveTab] = useState(normalizedInitialTab);
  const [internalOpen, setInternalOpen] = useState(isOpen);
  const user = authManager.getCurrentUser();
  const { isLightTheme } = useTheme();
  
  const [firestoreProfile, setFirestoreProfile] = useState(null);
  const [profileDataLoading, setProfileDataLoading] = useState(false);
  const [initialPanelLoadComplete, setInitialPanelLoadComplete] = useState(false);

  const userTier = authState.tier;

  useEffect(() => {
    const unsubscribe = authManager.subscribe(setAuthState);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setInternalOpen(true);
      setInitialPanelLoadComplete(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (internalOpen && normalizedInitialTab) {
      setActiveTab(normalizedInitialTab);
    }
  }, [internalOpen, normalizedInitialTab]);

  useEffect(() => {
    const initializePanelData = async () => {
      if (internalOpen && user?.uid && !initialPanelLoadComplete) {
        if (!firestoreProfile || firestoreProfile.uid !== user.uid) {
          setProfileDataLoading(true);
          try {
            const profile = await authManager.loadUserProfile(user.uid);
            setFirestoreProfile(profile);
          } catch (err) {
            console.error("Error loading user profile in SettingsPanel:", err);
          } finally {
            setProfileDataLoading(false);
          }
        }
        
        setInitialPanelLoadComplete(true);
      } else if (!user?.uid && internalOpen) {
        setFirestoreProfile(null);
        setInitialPanelLoadComplete(true);
      }
    };

    initializePanelData();
  }, [internalOpen, user?.uid, initialPanelLoadComplete, firestoreProfile]);

  const isOnFreeTrial = useMemo(() =>
    !profileDataLoading && firestoreProfile?.freetrial === true && userTier === 'Pro',
    [firestoreProfile, userTier, profileDataLoading]
  );

  const trialEndsAt = useMemo(() =>
    firestoreProfile?.trialEndsAt ? new Date(firestoreProfile.trialEndsAt) : null,
    [firestoreProfile]
  );

  const daysRemaining = useMemo(() => {
    if (!trialEndsAt) return 0;
    const now = new Date();
    const diffTime = trialEndsAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [trialEndsAt]);

  useEffect(() => {
    if (!internalOpen) {
      setActiveTab(normalizedInitialTab);
      setFirestoreProfile(null);
      setInitialPanelLoadComplete(false);
    }
  }, [internalOpen, normalizedInitialTab]);

  const handleContentClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    const handleOpenSubscription = () => {
      setInternalOpen(true);
      setActiveTab(TABS.SUBSCRIPTION);
    };
    window.addEventListener('shelbula:open-settings-subscription', handleOpenSubscription);
    return () => window.removeEventListener('shelbula:open-settings-subscription', handleOpenSubscription);
  }, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab === 'preferences' ? TABS.PROFILE : tab);
  }, []);

  const handleClose = useCallback(() => {
    setInternalOpen(false);
  }, []);

  useEffect(() => {
    if (!internalOpen && isOpen) {
      onClose?.();
    }
  }, [internalOpen, isOpen, onClose]);

  if (internalOpen && !initialPanelLoadComplete && profileDataLoading) {
    return (
      <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/10 backdrop-blur-sm">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <AnimatePresence onExitComplete={onClose}>
      {internalOpen && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/30 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={handleContentClick}
            className={`relative w-full h-full sm:h-[clamp(500px,85vh,700px)] sm:max-w-4xl sm:rounded-xl overflow-hidden border-0 sm:border border-border-color shadow-glass ${isLightTheme ? 'bg-white' : 'bg-surface'}`}
          >
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 sm:p-4 border-b border-border-color bg-inherit z-10 h-[60px] sm:h-[60px]">
              <div className="flex items-center gap-4 sm:gap-6 flex-1 min-w-0">
                <CatLogo size={24} />
                <nav className="flex gap-4 sm:gap-6 overflow-x-auto pb-1 -mb-1 hide-scrollbar flex-1 min-w-0">
                  {Object.entries(TABS).map(([key, value]) => (
                    <button
                      key={key}
                      onClick={() => handleTabChange(value)}
                      className={`text-sm transition-colors duration-150 pb-1 border-b-2 whitespace-nowrap flex-shrink-0
                        ${activeTab === value 
                          ? 'text-primary-light font-medium border-primary-light' 
                          : 'text-on-surface/60 hover:text-on-surface/80 border-transparent hover:border-on-surface/30'}`}
                    >
                      {TAB_DISPLAY_NAMES[key]}
                    </button>
                  ))}
                </nav>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-surface-dark/50 rounded-md transition-colors duration-150 flex-shrink-0 ml-2"
                aria-label="Close settings"
              >
                <X className="w-5 h-5 text-on-surface" />
              </button>
            </div>

            <div className="h-full pt-[60px] overflow-hidden">
              <div className="h-full overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
                <AnimatePresence mode="wait">
                  {activeTab === TABS.PROFILE && user && (
                    <motion.div
                      key="profile"
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -15 }}
                      transition={{ duration: 0.25 }}
                      className="min-h-full"
                    >
                      <ProfileSection
                        user={user}
                        userTier={userTier}
                        onTabChange={handleTabChange}
                        isOnFreeTrial={isOnFreeTrial}
                        daysRemaining={daysRemaining}
                        trialEndsAt={trialEndsAt}
                        profileLoading={profileDataLoading || !firestoreProfile}
                        firestoreProfileData={firestoreProfile}
                      />
                    </motion.div>
                  )}
                  {activeTab === TABS.API_KEYS && (
                    <motion.div
                      key="api-keys"
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -15 }}
                      transition={{ duration: 0.25 }}
                      className="min-h-full"
                    >
                      <APIKeysSection onClose={handleClose} />
                    </motion.div>
                  )}
                  {activeTab === TABS.SUBSCRIPTION && user && (
                    <motion.div
                      key="subscription"
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -15 }}
                      transition={{ duration: 0.25 }}
                      className="min-h-full"
                    >
                      <SubscriptionSection
                        user={user}
                        dbUserProfile={firestoreProfile}
                        userTier={userTier}
                        isOnFreeTrial={isOnFreeTrial}
                        daysRemaining={daysRemaining}
                        trialEndsAt={trialEndsAt}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default SettingsPanel;